/**
 * InsufficientTokensModal
 *
 * Modal that appears when a user tries to perform an operation that
 * requires more tokens than they have. Shows token packages for quick purchase.
 *
 * Design: Clean, direct modal without "Claim Free" button (free tokens are auto-credited).
 */

import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, X, CreditCard, Loader2, Bitcoin } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTokenBalance } from '../../hooks/useTokenBalance';
import { useTranslation } from '../../hooks/useTranslation';
import { fetchPackages as apiFetchPackages, createCheckout } from '../../services/paymentApi';
import type { TokenPackage } from '../../services/paymentApi';

import { handleError } from '../../utils/errorHandler';

// ============================================================================
// Types
// ============================================================================

interface InsufficientTokensModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /**
   * Number of tokens required for the operation, or `null` when the surface
   * that opened this modal has no quote to show.
   *
   * `null` happens on a server refusal (HTTP 402) raised by a surface that
   * displays no cost card: the 402 body carries the shortfall only inside its
   * prose message, and parsing prose to recover a number is exactly the kind of
   * invention this codebase keeps paying to remove. The deficit line is omitted
   * instead — the balance and the packages are still shown, which is what the
   * user needs to act.
   */
  requiredTokens: number | null;
  /** Human-readable name of the operation */
  operationName: string;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Format token count for display (e.g., 1000000 -> "1M", 5000 -> "5K")
 */
function formatTokenCount(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(0)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(0)}K`;
  }
  return tokens.toString();
}

// ============================================================================
// Component
// ============================================================================

export function InsufficientTokensModal({
  isOpen,
  onClose,
  requiredTokens,
  operationName,
}: InsufficientTokensModalProps) {
  const { t } = useTranslation();
  const { user, getIdToken } = useAuth();
  const { balance } = useTokenBalance();
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const deficit =
    requiredTokens === null || balance === null ? null : Math.max(0, requiredTokens - balance);

  // Fetch packages on open
  const fetchPackages = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiFetchPackages();
      setPackages(result.packages);
    } catch (err) {
      console.error('Failed to load packages:', err);
      setError(handleError(err, 'InsufficientTokensModal', 'Failed to load packages'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen, fetchPackages]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle package purchase
  const handlePurchase = async (packageId: string) => {
    if (!user) {
      setError('Please sign in to purchase tokens');
      return;
    }

    setPurchasing(packageId);
    setError(null);

    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const details = await createCheckout({
        packageId,
        authToken: token,
        successUrl: `${window.location.origin}${window.location.pathname}?tokens_purchased=true`,
        cancelUrl: window.location.href,
      });

      // Redirect to payment checkout if checkout_url is returned
      const checkoutUrl = (details as unknown as Record<string, string>).checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      setError(handleError(err, 'InsufficientTokensModal', 'Failed to start checkout'));
      setPurchasing(null);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-secondary-800 rounded-2xl max-w-2xl w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="relative p-4 sm:p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-secondary-900 dark:text-white">
                {t('insufficientTokens.title')}
              </h2>
              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                {deficit === null
                  ? t('insufficientTokens.subtitleNoQuote', {
                      operation: operationName,
                      defaultValue: 'You do not have enough tokens for {{operation}}',
                    })
                  : t('insufficientTokens.subtitle', {
                      deficit: deficit.toLocaleString(),
                      operation: operationName,
                    })}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Token Summary */}
          <div className="bg-secondary-50 dark:bg-secondary-900 rounded-xl p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
              {requiredTokens !== null && (
                <span className="text-secondary-600 dark:text-secondary-400">
                  {t('insufficientTokens.required')}:{' '}
                  <strong>{requiredTokens.toLocaleString()}</strong> {t('tokens.tokens')}
                </span>
              )}
              <span className="text-secondary-600 dark:text-secondary-400">
                {t('insufficientTokens.yourBalance')}:{' '}
                <strong>{balance === null ? '—' : balance.toLocaleString()}</strong>{' '}
                {t('tokens.tokens')}
              </span>
            </div>
          </div>

          {/* Crypto Coming Soon Banner */}
          {!loading && packages.some((p) => p.available === false) && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-3">
                <Bitcoin className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-800 dark:text-amber-200 text-sm">
                    Crypto Payments Coming Soon!
                  </h4>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                    We're upgrading to accept Bitcoin, Ethereum, USDT, and more.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Package Selection */}
          <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-4">
            {t('insufficientTokens.choosePackage')}
          </h3>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : error && packages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={fetchPackages}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors min-h-[44px]"
              >
                {t('tokens.retry')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {packages.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => pkg.available !== false && handlePurchase(pkg.id)}
                  disabled={purchasing !== null || pkg.available === false}
                  className={`relative p-3 sm:p-4 border-2 rounded-xl text-center transition-all min-h-[140px] ${
                    pkg.available === false
                      ? 'border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800 cursor-not-allowed'
                      : pkg.popular
                        ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:border-primary-500 hover:shadow-lg'
                        : 'border-secondary-200 dark:border-secondary-700 hover:border-primary-400 hover:shadow-lg hover:bg-secondary-50 dark:hover:bg-secondary-750'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {/* Popular Badge */}
                  {pkg.popular && pkg.available !== false && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-full whitespace-nowrap">
                      {t('insufficientTokens.popular')}
                    </span>
                  )}

                  {/* Crypto Soon Badge for unavailable */}
                  {pkg.available === false && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-amber-500 text-white text-xs font-medium rounded-full whitespace-nowrap flex items-center gap-1">
                      <Bitcoin className="h-3 w-3" />
                      Soon
                    </span>
                  )}

                  {purchasing === pkg.id ? (
                    <div className="py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-primary-500 mx-auto" />
                    </div>
                  ) : (
                    <>
                      <p
                        className={`text-xl sm:text-2xl font-bold ${pkg.available === false ? 'text-secondary-400 dark:text-secondary-500' : 'text-secondary-900 dark:text-white'}`}
                      >
                        ${pkg.price_usd}
                      </p>
                      <p
                        className={`text-sm font-medium ${pkg.available === false ? 'text-secondary-400 dark:text-secondary-500' : 'text-secondary-700 dark:text-secondary-300'}`}
                      >
                        {formatTokenCount(pkg.tokens)}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        {t('tokens.tokens')}
                      </p>
                      {pkg.discount_percent > 0 && pkg.available !== false && (
                        <span className="inline-block mt-2 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium rounded">
                          {t('insufficientTokens.off', { percent: pkg.discount_percent })}
                        </span>
                      )}
                    </>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Error Message */}
          {error && packages.length > 0 && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400 text-sm mb-4">
              {error}
            </div>
          )}

          {/* Cancel Button */}
          <button
            onClick={onClose}
            className="w-full py-3 border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 font-medium rounded-xl hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors min-h-[44px]"
          >
            {t('insufficientTokens.cancel')}
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900 rounded-b-2xl">
          <div className="flex items-center justify-center gap-2 text-xs text-secondary-500 dark:text-secondary-400">
            <CreditCard className="h-3.5 w-3.5 flex-shrink-0" />
            <span>
              {t('insufficientTokens.purchasedNeverExpire')} •{' '}
              {t('insufficientTokens.securePayment')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InsufficientTokensModal;
